import axios from "axios";
import { NotImplementedError, ObjType } from "../../types";
import { servicePersonaToPersona, upsertPersonaRequestToServiceUpsertPersonaRequest } from "../../models/persona";
import { PromiseHandler } from "../../handlers";
import { PersonaGenerateFailed, PersonaGenerationBlocked, PersonasFetchFailed, PersonaCreationFailed, PersonaUpdateFailed, PersonaMintIdFailed } from "./errors";
/**
 * Personas Module - AI-Generated Audience Personas
 *
 * This module provides comprehensive persona management capabilities:
 * - AI-powered persona generation from audience descriptions
 * - CRUD operations for personas (find, save)
 * - Service transforms (backend ↔ frontend data conversion)
 * - Error handling with specific error types
 * - ID minting for new records
 * - Caching for optimized performance
 *
 * Special Features:
 * - generate() method creates personas with IDs that don't yet exist in DB
 * - saveItem() checks DB existence before determining POST vs PATCH
 */
class Personas extends PromiseHandler {
    constructor(sdk) {
        const personasUrl = `${sdk.urls.baseAPIUrl}/persona`;
        super(sdk, "personas", { atomize: true });
        this.url = {
            generate: `${sdk.urls.baseAPIUrl}/draft-persona`,
            personas: personasUrl,
            single: (id) => `${personasUrl}/${id}`
        };
    }
    /**
     * Generates a persona from an audience description using AI.
     * This is a domain-specific method unique to the personas module.
     * Note: The generated persona includes an ID but is not yet persisted to the database.
     * Use save() to persist the generated persona.
     * @param request Generation request with audience description/keywords and type
     * @returns Promise resolving to the generated persona
     * @throws PersonaGenerationBlocked if the request contains protected terms
     * @throws PersonaGenerateFailed if generation fails or no data returned
     */
    async generate(request) {
        try {
            const { data: res } = await axios.post(this.url.generate, { query: request.audienceDescription }, {
                headers: {
                    "Content-Type": "application/json"
                }
            });
            if (res.validation?.status === "BLOCKED") {
                throw new PersonaGenerationBlocked(res.validation.blocked_reason || "Request was blocked");
            }
            if (res.data) {
                return servicePersonaToPersona(res.data);
            }
            throw new PersonaGenerateFailed({}, "No persona data returned");
        }
        catch (error) {
            if (error instanceof PersonaGenerationBlocked) {
                throw error;
            }
            throw new PersonaGenerateFailed(error);
        }
    }
    /**
     * Finds personas. Returns all personas for the current organization.
     * This is a required PromiseHandler method implementation.
     * @returns Promise resolving to the list of personas
     * @throws PersonasFetchFailed if fetch operation fails
     */
    async findItems() {
        try {
            return this.getPersonas();
        }
        catch (error) {
            if (error instanceof PersonasFetchFailed) {
                throw error;
            }
            throw new PersonasFetchFailed(error);
        }
    }
    /**
     * Fetch a single persona by ID.
     * Note: Called from both findItems() and saveItem() for DB existence checks.
     * @param id Persona ID to fetch
     * @returns Promise resolving to array with single persona (or empty if not found)
     * @throws PersonasFetchFailed if fetch operation fails (excludes 404 which returns empty array)
     */
    async getPersona(id) {
        const url = this.url.single(id);
        try {
            return await this.cache.promise(url, () => axios
                .get(url, {
                headers: {
                    "Content-Type": "application/json"
                }
            })
                .then(({ data: { data } = {} }) => (data ? [servicePersonaToPersona(data)] : [])));
        }
        catch (error) {
            // Treat 404 as "persona not found" rather than as a fetch failure.
            if (axios.isAxiosError(error) && error.response?.status === 404) {
                return [];
            }
            throw new PersonasFetchFailed(error);
        }
    }
    /**
     * Fetch all personas for the organization.
     * @returns Promise resolving to array of personas
     * @throws PersonasFetchFailed if fetch operation fails
     */
    async getPersonas() {
        const url = this.url.personas;
        try {
            return await this.cache.promise(url, () => axios
                .get(url, {
                headers: {
                    "Content-Type": "application/json"
                }
            })
                .then(({ data: { data } = {} }) => (data || []).map(servicePersonaToPersona)));
        }
        catch (error) {
            throw new PersonasFetchFailed(error);
        }
    }
    /**
     * Saves a persona by creating or updating it.
     * Demonstrates full save lifecycle with ID minting, transforms, and error handling.
     *
     * Special behavior: Unlike typical modules, this checks DB existence before determining
     * POST vs PATCH because generate() returns personas with IDs that don't yet exist in DB.
     *
     * @param data Persona data to save
     * @returns Promise resolving to the saved persona
     * @throws PersonaCreationFailed if create operation fails
     * @throws PersonaUpdateFailed if update operation fails
     * @throws PersonaMintIdFailed if ID minting fails
     */
    async saveItem(data) {
        let personaExists = false;
        // If data already has an ID, check if it exists in DB first
        // This caches the result and avoids checking for freshly-minted IDs
        if (data.id) {
            const result = await this.getPersona(data.id);
            personaExists = result.length > 0;
        }
        // Prepare persona for saving (mint ID if needed)
        const personaToSave = await this.make(data);
        if (!personaToSave.id) {
            throw new PersonaMintIdFailed("Failed to generate persona ID");
        }
        const id = personaToSave.id;
        const serviceRequest = upsertPersonaRequestToServiceUpsertPersonaRequest(personaToSave);
        // Perform the save operation (POST or PATCH)
        try {
            const saveMethod = personaExists ? "patch" : "post";
            const url = personaExists ? this.url.single(id) : this.url.personas;
            const { data: res } = await axios[saveMethod](url, serviceRequest, {
                headers: {
                    "Content-Type": "application/json"
                }
            });
            const savedPersona = res.data ? servicePersonaToPersona(res.data) : personaToSave;
            this.cache.reconcile("save", [savedPersona]);
            return savedPersona;
        }
        catch (apiError) {
            if (personaExists) {
                throw new PersonaUpdateFailed(apiError);
            }
            else {
                throw new PersonaCreationFailed(apiError);
            }
        }
    }
    /**
     * Prepares a persona for saving, minting ID if new.
     * Note: Accepts UpsertPersonaRequest which may include publishSegments field.
     * This field is preserved for the service transformation step.
     * @param persona Partial persona data
     * @returns Complete persona with ID
     * @throws PersonaMintIdFailed if ID minting fails
     */
    async make(persona) {
        let id = this.getIdentifier(persona);
        const isNewPersona = typeof id === "undefined";
        if (isNewPersona) {
            id = await this.sdk.cryptography.mintKey(ObjType.AI_AUDIENCE_PERSONA);
            if (typeof id === "undefined") {
                throw new PersonaMintIdFailed();
            }
        }
        return {
            ...persona,
            id
        };
    }
    /**
     * Deletion is not supported for personas.
     * @param _id Persona ID (unused, required by interface)
     * @throws NotImplementedError always, as deletion is not supported
     */
    async deleteItem(_id) {
        throw new NotImplementedError("Personas cannot be deleted");
    }
}
export default Personas;
//# sourceMappingURL=index.js.map